import { useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
export const useQueryParams = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const params = new URLSearchParams(location.search);

  const defaultParams = {
    hidedetails: undefined,
    search: '',
    object: undefined,
    filter: '',
    filterObjects: undefined,
    filterControls: {},
    tab: undefined,
    sort: undefined,
    sortDirection: 'asc',
    sortGroupBy: undefined,
    selectedItems: undefined,
    expandedItems: undefined,
    popupTableItems: undefined,
    nodeSize: undefined,
    tablePage: 1,
    tableScroll: 0,
    tableViewMode: undefined,
    schemaChangesOnly: false,
    technicalDefinitionViewMode: 'sql',
  };

  const paramHideDetailsPane =
    location.state?.hidedetails || params.get('hidedetails') || defaultParams.hidedetails;
  const paramSearchTerm = location.state?.search || params.get('search') || defaultParams.search;
  const paramTab = location.state?.tab || params.get('tab') || defaultParams.tab;
  const paramObject = params.get('object') || defaultParams.object;
  const paramFilterTerm = location.state?.filter || params.get('filter') || defaultParams.filter;
  const paramSort = location.state?.sort?.key || params.get('sort') || defaultParams.sort;
  const paramSortDirection =
    location.state?.sort?.direction || params.get('sortdirection') || defaultParams.sortDirection;
  const paramSortGroupBy =
    location.state?.sort?.groupByColumn || params.get('sortgroupby') || defaultParams.sortGroupBy;
  const paramTablePage =
    location.state?.tablePage || params.get('tablepage') || defaultParams.tablePage;
  const paramTableScroll = location.state?.tableScroll || defaultParams.tableScroll;
  const paramTableViewMode =
    location.state?.tableViewMode || params.get('tableviewmode') || defaultParams.tableViewMode;
  const paramSchemaChangesOnly =
    location.state?.schemaChangesOnly ||
    params.get('schemachangesonly') ||
    defaultParams.schemaChangesOnly;
  const paramTechnicalDefinitionViewMode =
    location.state?.technicalDefinitionViewMode ||
    params.get('technicaldefinitionviewmode') ||
    defaultParams.technicalDefinitionViewMode;
  const paramSelectedItems = location.state?.selectedItems || defaultParams.selectedItems;
  const paramExpandedItems = location.state?.expandedItems || defaultParams.expandedItems;
  const paramPopupTableItems = location.state?.popupTableItems || defaultParams.popupTableItems;
  const paramNodeSize = location.state?.nodeSize || defaultParams.nodeSize;
  const paramFilterObjects = location.state?.filterObjects || defaultParams.filterObjects;
  const paramFilterControls = location.state?.filterControls || defaultParams.filterControls;

  const queryParams = {
    hidedetails: paramHideDetailsPane === true || paramHideDetailsPane === 'true' ? true : false,
    search: paramSearchTerm,
    object: paramObject,
    filter: paramFilterTerm,
    filterObjects: paramFilterObjects,
    filterControls: paramFilterControls,
    tab: paramTab,
    sort: paramSort
      ? { key: paramSort, direction: paramSortDirection, groupByColumn: paramSortGroupBy }
      : null,
    selectedItems: paramSelectedItems,
    expandedItems: paramExpandedItems,
    popupTableItems: paramPopupTableItems,
    nodeSize: paramNodeSize,
    tablePage: paramTablePage && parseInt(paramTablePage),
    tableScroll: paramTableScroll,
    tableViewMode: paramTableViewMode,
    schemaChangesOnly:
      paramSchemaChangesOnly === true || paramSchemaChangesOnly === 'true' ? true : false,
    technicalDefinitionViewMode: paramTechnicalDefinitionViewMode,
  };

  //const [_queryParams, setQueryParams] = useState(queryParams)
  //const [_params, setParams] = useState(params)

  const setHistory = obj => {
    // Must do this to ensure latest location and params always are used by setHistory.
    // setHistory is not always re-rendered when this hook is re-rendered,
    // which will cause the setHistory function to use old location and thus result in unexpected behaviour.
    const location = locationRef.current;
    const params = new URLSearchParams(location.search);

    if (obj.hidedetails && obj.hidedetails !== params.get('hidedetails')) {
      params.delete('hidedetails');
    }

    if (obj.search !== params.get('search')) {
      params.delete('search');
    }

    if (obj.tab && obj.tab !== params.get('tab')) {
      params.delete('tab');
      params.delete('object');
      params.delete('filter');
      params.delete('sort');
      params.delete('sortdirection');

      params.append('tab', obj.tab);
    }

    if (obj.object && obj.object !== params.get('object')) {
      params.delete('object');
      params.delete('filter');
      params.delete('sort');
      params.delete('sortdirection');

      params.append('object', obj.object);
    }

    if (obj.filter && obj.filter !== params.get('filter')) {
      params.delete('filter');
    }

    if (obj.sort && obj.sort.key !== params.get('sort')) {
      params.delete('sort');
      params.delete('sortdirection');
      params.delete('sortgroupby');
    }

    if (obj.sort && obj.sort.direction !== params.get('sortdirection')) {
      params.delete('sortdirection');
    }

    if (obj.sort && obj.sort.sortgroupby !== params.get('sortgroupby')) {
      params.delete('sortgroupby');
    }

    if (obj.tablePage !== params.get('tablepage')) {
      params.delete('tablepage');
    }

    if (obj.tableViewMode && obj.tableViewMode !== params.get('tableviewmode')) {
      params.set('tableviewmode', obj.tableViewMode);
    }

    if (obj.schemaChangesOnly && obj.schemaChangesOnly !== params.get('schemachangesonly')) {
      params.delete('schemachangesonly');
    }

    if (
      obj.technicalDefinitionViewMode &&
      obj.technicalDefinitionViewMode !== params.get('technicaldefinitionviewmode')
    ) {
      params.delete('technicaldefinitionviewmode');
    }

    //flushSync( () => {
    //  setQueryParams(prev => ({
    //    ...prev,
    //    ...obj
    //  }))
    //})

    //setParams(params)

    navigate(location.pathname + '?' + params.toString(), {
      state: { ...location.state, ...obj },
      replace: true,
    });
  };
  // This causes restoration of URL upon login to fail.
  // This was added to do a flushSync in the setHistory function.
  // The reason for this was some bug where some properties were not saved correctly, cannot remember which ones now.
  // Saving this for reference if the bug shows up again.
  //useEffect( () => {
  // Replace url
  //navigate(location.pathname + "?" + _params.toString(), { state: { ...location.state, ..._queryParams }, replace: true })
  //}, [_queryParams, _params])

  const resetParams = (obj = {}) => {
    setHistory({ ...defaultParams, ...obj });
  };

  return { defaultParams, queryParams, setHistory, resetParams };
};
